import maplibregl from 'maplibre-gl';

/**
 * Utility functions for highlighting and zooming to features on the map
 */

/**
 * Calculate bounds for different geometry types
 */
const calculateBounds = (geometry: any): [number, number, number, number] => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  const addCoordinatesToBounds = (coordinates: any) => {
    if (Array.isArray(coordinates[0])) {
      // Multi-dimensional array (LineString, Polygon, etc.)
      coordinates.forEach((coord: any) => addCoordinatesToBounds(coord));
    } else {
      // Single coordinate pair
      const [x, y] = coordinates;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  };

  switch (geometry.type) {
    case 'Point':
      const [x, y] = geometry.coordinates;
      // Add small padding for Point
      const padding = 0.001;
      return [x - padding, y - padding, x + padding, y + padding];
    case 'LineString':
    case 'MultiPoint':
      addCoordinatesToBounds(geometry.coordinates);
      break;
    case 'Polygon':
    case 'MultiLineString':
      geometry.coordinates.forEach((ring: any) => addCoordinatesToBounds(ring));
      break;
    case 'MultiPolygon':
      geometry.coordinates.forEach((polygon: any) =>
        polygon.forEach((ring: any) => addCoordinatesToBounds(ring))
      );
      break;
  }

  return [minX, minY, maxX, maxY];
};

/**
 * Clean up existing blink layers to avoid conflicts
 */
const cleanupBlinkLayers = (map: maplibregl.Map) => {
  const sourceId = 'blink-highlight';
  const fillLayerId = 'blink-highlight-fill';
  const lineLayerId = 'blink-highlight-line';
  const circleLayerId = 'blink-highlight-circle';

  try {
    if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId);
    if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId);
    if (map.getLayer(circleLayerId)) map.removeLayer(circleLayerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);
  } catch (error) {
    // Ignore cleanup errors
  }
};

/**
 * Main function to zoom to and highlight a feature on the map
 * Nhấp nháy 3 lần rồi biến mất hoàn toàn
 */
export const zoomToFeature = async (geometry: any, map?: maplibregl.Map) => {
  const mapInstance = map || window.map;

  if (!mapInstance || !geometry) {
    console.warn('Map instance or geometry not available');
    return;
  }

  try {
    // Parse geometry if it's a string
    const geom = typeof geometry === 'string' ? JSON.parse(geometry) : geometry;

    // Clean up any existing blink layers
    cleanupBlinkLayers(mapInstance);

    // Calculate bounds and zoom to feature
    const bbox = calculateBounds(geom);

    mapInstance.fitBounds(bbox as any, {
      duration: 500,
      padding: 50,
      maxZoom: 18,
    });

    // Wait for zoom to complete
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Setup source and data
    const sourceId = 'blink-highlight';
    const fillLayerId = 'blink-highlight-fill';
    const lineLayerId = 'blink-highlight-line';
    const circleLayerId = 'blink-highlight-circle';

    const feature = {
      type: 'Feature',
      geometry: geom,
      properties: {}
    };

    mapInstance.addSource(sourceId, {
      type: 'geojson',
      data: feature as any,
    });

    // Nhấp nháy 3 lần
    for (let i = 0; i < 3; i++) {
      // Add appropriate layer based on geometry type
      if (geom.type.toLowerCase().includes('point')) {
        // Point geometry
        mapInstance.addLayer({
          id: circleLayerId,
          type: 'circle',
          source: sourceId,
          paint: {
            'circle-color': '#ff4444',
            'circle-radius': 8,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-opacity': 0.8,
          },
        });
      } else if (geom.type.toLowerCase().includes('line')) {
        // LineString geometry
        mapInstance.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#44ff44',
            'line-width': 5,
            'line-opacity': 1,
          },
        });
      } else {
        // Polygon geometry - add both fill and stroke
        mapInstance.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': 'cyan',
            'fill-opacity': 0.5,
          },
        });
        mapInstance.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': 'cyan',
            'line-width': 5,
            'line-opacity': 1,
          },
        });
      }

      // Wait 200ms (show)
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Remove layers (hide)
      try {
        if (mapInstance.getLayer(fillLayerId)) mapInstance.removeLayer(fillLayerId);
        if (mapInstance.getLayer(lineLayerId)) mapInstance.removeLayer(lineLayerId);
        if (mapInstance.getLayer(circleLayerId)) mapInstance.removeLayer(circleLayerId);
      } catch (error) {
        // Ignore removal errors
      }

      // Wait 200ms (hide)
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    // Final cleanup
    cleanupBlinkLayers(mapInstance);

  } catch (error) {
    console.error('Error highlighting feature on map:', error);
    // Ensure cleanup even on error
    try {
      cleanupBlinkLayers(mapInstance);
    } catch (cleanupError) {
      // Ignore cleanup errors
    }
  }
};

export default {
  zoomToFeature
};